<template>
  <div
    v-if="show"
    ref="yearsGridContainer"
    :class="`years-grid years-grid--${size}`"
    @click.stop
  >
    <div
      v-for="year in yearOptions"
      :key="year"
      :class="clsx('year-item', {
        'current-year': year === selectedYear,
        'today-year': isCurrentYearToday(year),
        'in-range': isInYearRange(year),
        'range-start': isYearRangeStart(year),
        'range-end': isYearRangeEnd(year),
        'is-disabled': isDisabledYear(year)
      })"
      @click="selectYear(year)"
      :aria-label="`Select year ${year}`"
      :ref="year === selectedYear ? 'selectedYearItem' : null"
    >
      {{ year }}
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';
import { moment } from '../../lib';

export default {
  props: {
    /**
     * Controls the visibility of the year grid.
     * @type {boolean}
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * Defines the size of the year grid.
     * @type {string}
     * @values 'normal', 'small'
     */
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value)
    },
    /**
     * An array of year numbers to display in the grid.
     * This array is typically generated by the parent component.
     * @type {Array<number>}
     * @required
     */
    yearOptions: {
      type: Array,
      required: true,
    },
    /**
     * The currently selected year.
     * Used to determine the 'current-year' highlight.
     * @type {number}
     * @required
     */
    selectedYear: {
      type: Number,
      required: true,
    },
    /**
     * If true, the calendar is in range selection mode.
     * @type {boolean}
     */
    range: {
      type: Boolean,
      default: false,
    },
    /**
     * The start date of a selected range (Moment.js object or Date).
     * Only relevant when `range` is true.
     * @type {object | Date}
     */
    startDate: {
      type: [Object, Date],
      default: null,
    },
    /**
     * The end date of a selected range (Moment.js object or Date).
     * Only relevant when `range` is true.
     * @type {object | Date}
     */
    endDate: {
      type: [Object, Date],
      default: null,
    },
    /**
     * The maximum number of units (days, weeks or months) allowed in a selected range (inclusive).
     * If null, there is no limit.
     * @type {number | null}
     */
    maxRangeLimit: {
      type: Number,
      default: null,
    },
    /**
     * The unit for the maxRangeLimit ('day', 'week', or 'month').
     * @type {string}
     * @values 'day', 'week', 'month'
     */
    limitUnit: {
      type: String,
      default: 'month',
      validator: (value) => ['day', 'week', 'month'].includes(value),
    },
    /**
     * Internal state indicating if a range selection is currently in progress (startDate set, endDate not).
     * @type {boolean}
     */
    selectingRange: {
      type: Boolean,
      default: false,
    },
    availableDates: {
      type: Array,
      default: () => [],
      validator: (value) => value?.every((item) => {
        return item && (typeof item === 'string' || item instanceof Date || (typeof item === 'object' && item.date));
      }),
    },
  },
  data() {
    return {
      centeredOnOpen: false,
    };
  },
  mixins: [ moment ],
  mounted() {
    if (this.show) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.centerSelectedYear();
        }, 50);
      });
    }
  },
  methods: {
    clsx,
    selectYear(year) {
      if (!this.isDisabledYear(year)) {
        this.$emit('select-year', year);
      }
    },
    isCurrentYearToday(year) {
      return year === this.getYearMoment(this.getMoment());
    },
    getMomentForMonth(monthIndex, year) {
      const date = new Date(year, monthIndex, 1);
      return this.getMoment(date);
    },
    getMonthsDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);

      const yearsDiff = d2.getFullYear() - d1.getFullYear();
      const monthsDiff = d2.getMonth() - d1.getMonth();

      return (yearsDiff * 12) + monthsDiff + 1;
    },
    getDaysDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);
      const diffTime = Math.abs(d2.getTime() - d1.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1;
    },
    getWeeksDifference(startMoment, endMoment) {
      const m1 = this.startOfMoment(this.getMoment(startMoment), 'isoWeek');
      const m2 = this.startOfMoment(this.getMoment(endMoment), 'isoWeek');

      if (window.moment) {
        return Math.abs(m1.diff(m2, 'weeks')) + 1;
      }

      const d1 = this.toDateMoment(m1);
      const d2 = this.toDateMoment(m2);
      const diffTime = Math.abs(d2.getTime() - d1.getTime());
      const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));

      return diffWeeks + 1;
    },
    isInYearRange(year) {
      if (!this.range || !this.startDate || this.selectingRange) {
        return false;
      }

      const isEndDateValidAndPresent = (window.moment && this.endDate)
        ? (window.moment.isMoment(this.endDate) && this.endDate.isValid())
        : (this.endDate instanceof Date && !isNaN(this.endDate.getTime()));

      if (!isEndDateValidAndPresent) {
        return false;
      }

      const startYear = this.getYearMoment(this.startDate);
      const endYear = this.getYearMoment(this.endDate);
      return year >= startYear && year <= endYear;
    },
    isYearRangeStart(year) {
      return this.range && this.startDate && year === this.getYearMoment(this.startDate);
    },
    isYearRangeEnd(year) {
      return this.range && this.endDate && year === this.getYearMoment(this.endDate);
    },
    isYearAvailable(year) {
      if (this.availableDates.length === 0) {
        return true;
      }

      return this.availableDates.some((availableDateItem) => {
        const comparableDate = availableDateItem.date || availableDateItem;
        const dateMoment = this.getMoment(comparableDate);

        return this.getYearMoment(dateMoment) === year;
      });
    },
    isDisabledYear(yearToCheck) {
      if (!this.isYearAvailable(yearToCheck)) {
        return true;
      }

      if (!this.range || !this.selectingRange || this.maxRangeLimit === null || !this.startDate) {
        return false;
      }

      const startMoment = this.getMoment(this.startDate);
      const startYear = this.getYearMoment(startMoment);

      let calculatedDifference;
      if (yearToCheck > startYear) {
        const potentialStartOfFutureYear = this.getMoment(new Date(yearToCheck, 0, 1));

        switch (this.limitUnit) {
          case 'month':
            calculatedDifference = this.getMonthsDifference(startMoment, potentialStartOfFutureYear);
            break;
          case 'week':
            calculatedDifference = this.getWeeksDifference(startMoment, potentialStartOfFutureYear);
            break;
          case 'day':
            calculatedDifference = this.getDaysDifference(startMoment, potentialStartOfFutureYear);
            break;
          default:
            calculatedDifference = this.getDaysDifference(startMoment, potentialStartOfFutureYear);
            break;
        }
        if (calculatedDifference > this.maxRangeLimit) {
          return true;
        }
      } else if (yearToCheck < startYear) {
        const potentialEndOfPastYear = this.getMoment(new Date(yearToCheck, 11, 31));

        switch (this.limitUnit) {
          case 'month':
            calculatedDifference = this.getMonthsDifference(potentialEndOfPastYear, startMoment);
            break;
          case 'week':
            calculatedDifference = this.getWeeksDifference(potentialEndOfPastYear, startMoment);
            break;
          case 'day':
            calculatedDifference = this.getDaysDifference(potentialEndOfPastYear, startMoment);
            break;
          default:
            calculatedDifference = this.getDaysDifference(potentialEndOfPastYear, startMoment);
            break;
        }
        if (calculatedDifference > this.maxRangeLimit) {
          return true;
        }
      }
      return false;
    },
    centerSelectedYear() {
      const selectedYearEl = this.$refs.selectedYearItem
        ? (Array.isArray(this.$refs.selectedYearItem) ? this.$refs.selectedYearItem[0] : this.$refs.selectedYearItem)
        : null;

      const containerEl = this.$refs.yearsGridContainer;

      if (selectedYearEl && containerEl) {
        const containerHeight = containerEl.clientHeight;
        const itemHeight = selectedYearEl.offsetHeight;
        const itemTop = selectedYearEl.offsetTop;
        containerEl.scrollTop = itemTop - (containerHeight / 2) + (itemHeight / 2);
      }
    },
  },
  watch: {
    show(isShowing) {
      if (isShowing) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.centerSelectedYear();
          }, 50);
        });
      }
    },
    selectedYear() {
      if (this.show) {
        this.$nextTick(() => {
          this.centerSelectedYear();
        });
      }
    }
  },
};
</script>

<style lang="scss" scoped>
$small-scale-factor: 0.8;
$months-years-grid-item-font-size: 18px;

.years-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background-color: #fff;
  overflow-y: auto;
  max-height: 300px;
  scroll-behavior: auto;

  .year-item {
    font-family: inherit;
    font-size: $months-years-grid-item-font-size;
    font-weight: 400;
    text-align: center;
    padding: 10px 12px;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;

    &:hover {
      background-color: #ededed;
    }

    &:active {
      background-color: #e0e0e0;
      transform: scale(0.98);
    }

    &.current-year {
      background-color: #f0f0f0;
      font-weight: 600;
    }

    &.today-year {
      background-color: #fb923c;
      color: white;
      font-weight: 600;
    }

    &.in-range {
      background-color: #EDEDED;
      font-weight: 500;
      color: inherit;
      &:hover {
        background-color: darken(#EDEDED, 5%);
      }
    }

    &.range-start,
    &.range-end {
      font-weight: 600;
    }

    &.is-disabled {
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.3;
      background-color: #FCE4E4;
      color: #999;

      &:hover {
        background-color: #FCE4E4;
      }
      &:active {
        transform: none;
      }
    }
  }

  &--small {
    .year-item {
      font-size: $months-years-grid-item-font-size * $small-scale-factor;
    }
  }
}
</style>
